import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Menu, X, Volume2, MessageCircle, Play } from 'lucide-react';
import { API } from '@/constants/data';
import { MusicContext } from '@/contexts/MusicContext';

const Header = () => {
  const { isPlaying, toggleMusic } = React.useContext(MusicContext);
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const [hasBlog, setHasBlog] = useState(false);

  useEffect(() => {
    axios.get(`${API}/blog?published_only=true`).then(r => setHasBlog(r.data.length > 0)).catch(() => {});
  }, []);

  const navLinks = [
    { name: 'Início', href: '/#inicio' },
    { name: 'Serviços', href: '/#servicos' },
    { name: 'Depoimentos', href: '/#depoimentos' },
    ...(hasBlog ? [{ name: 'Blog', href: '/blog' }] : []),
    { name: 'Contato', href: '/orcamento' },
  ];

  return (
    <header data-testid="header" className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? 'bg-white shadow-lg shadow-black/5 py-2' : 'bg-white py-3'}`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between">
          <Link to="/" data-testid="logo-link" className="flex-shrink-0 flex items-center gap-3">
            <img src="/promo/logo40v2-final-semtag.png" alt="Tomaselli" className={`transition-all duration-300 ${scrolled ? 'h-10 sm:h-12' : 'h-12 sm:h-16'}`} />
            <span className={`w-px bg-gray-200 transition-all ${scrolled ? 'h-8' : 'h-10'}`} />
            <span data-testid="header-tagline" style={{ fontFamily: "'Poppins', sans-serif" }} className="text-[10px] md:text-[12px] leading-snug font-bold tracking-[0.1em] md:tracking-[0.12em] uppercase text-[#0A2463] max-w-[170px] md:max-w-[230px]">
              Soluções em Audiovisual<br />e Eventos
            </span>
            <button onClick={(e) => { e.preventDefault(); toggleMusic(); }} data-testid="music-toggle-btn"
              className={`p-2 rounded-full transition-all border ${isPlaying ? 'bg-[#00A8E8]/15 border-[#00A8E8]/30 text-[#00A8E8]' : 'bg-gray-100 border-gray-200 text-gray-400 hover:text-[#0A2463]'}`}>
              {isPlaying ? <Volume2 size={16} /> : <Play size={16} />}
            </button>
          </Link>

          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              link.href.startsWith('/') ? (
                <Link key={link.name} to={link.href}
                  className="text-sm font-semibold tracking-wide transition-colors relative group text-[#0A2463]">
                  {link.name}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#00A8E8] transition-all duration-300 group-hover:w-full" />
                </Link>
              ) : (
                <a key={link.name} href={link.href}
                  className="text-sm font-semibold tracking-wide transition-colors relative group text-[#0A2463]">
                  {link.name}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#00A8E8] transition-all duration-300 group-hover:w-full" />
                </a>
              )
            ))}
          </nav>

          <a href="https://wa.me/5592988020092" target="_blank" rel="noopener noreferrer" data-testid="header-whatsapp-btn"
            className="hidden lg:flex items-center gap-2 bg-[#25D366] hover:bg-[#1EBE5A] text-white px-6 py-3 rounded-full text-sm font-bold transition-all shadow-lg shadow-green-500/20">
            <MessageCircle size={18} />
            WhatsApp
          </a>

          <button className="lg:hidden p-2 text-[#0A2463]" onClick={() => setIsOpen(!isOpen)} data-testid="mobile-menu-btn">
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
            className="lg:hidden absolute top-full left-0 right-0 bg-white shadow-xl border-t border-slate-100">
            <nav className="flex flex-col p-6 gap-4">
              {navLinks.map((link) => (
                link.href.startsWith('/') ? (
                  <Link key={link.name} to={link.href} className="text-[#0A2463] text-lg font-semibold" onClick={() => setIsOpen(false)} data-testid={`mobile-nav-${link.name.toLowerCase()}`}>{link.name}</Link>
                ) : (
                  <a key={link.name} href={link.href} className="text-[#0A2463] text-lg font-semibold" onClick={() => setIsOpen(false)} data-testid={`mobile-nav-${link.name.toLowerCase()}`}>{link.name}</a>
                )
              ))}
              <a href="https://wa.me/5592988020092" data-testid="mobile-whatsapp-btn"
                className="bg-[#25D366] text-white py-3 rounded-full text-center font-bold mt-4 flex items-center justify-center gap-2">
                <MessageCircle size={18} /> WhatsApp
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
